import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { createGoal, deleteGoal, updateGoal } from "wasp/client/operations";
import useTranslation from "../../../client/hooks/useTranslation";
import { Button } from "../../../client/components/ui/button";
import { Input } from "../../../client/components/ui/input";
import type { GoalWithHistory } from "../../../trading-goals/operations";
import { useLiteActions } from "../../shell/LiteActions";
import { LiteDrawer } from "../../ui/LiteDrawer";
import { Segmented, labelCls } from "../../ui/ui";

type GoalType = "pnl" | "win_rate" | "trades_count" | "custom";
type Period = "daily" | "weekly" | "monthly";

const UNIT: Record<Exclude<GoalType, "custom">, string> = { pnl: "$", win_rate: "%", trades_count: "" };

const isPlanLimit = (e: unknown) => {
  const err = e as { statusCode?: number; status?: number } | null;
  return err?.statusCode === 403 || err?.status === 403;
};

/** Création / édition d'un objectif : titre, type, cible, période. Les goals libres n'ont ni cible ni période. */
export function GoalDrawer({ open, goal, onClose }: {
  open: boolean;
  goal: GoalWithHistory | null;
  onClose: () => void;
}) {
  const t = useTranslation().lite.discipline;
  const { openUpgrade } = useLiteActions();
  const [title, setTitle] = useState("");
  const [type, setType] = useState<GoalType>("pnl");
  const [target, setTarget] = useState("");
  // "none" = goal ponctuel venu de l'Advanced : on ne lui impose une période que si l'utilisateur en choisit une.
  const [period, setPeriod] = useState<Period | "none">("daily");
  const [periodTouched, setPeriodTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(goal?.title ?? "");
    setType((goal?.type as GoalType) ?? "pnl");
    setTarget(goal && goal.type !== "custom" ? String(goal.targetValue) : "");
    setPeriod(goal ? (goal.recurrence as Period | "none") : "daily");
    setPeriodTouched(false);
    setError(null);
    setConfirmDelete(false);
  }, [open, goal]);

  const isCustom = type === "custom";
  const targetValue = Number(target.replace(",", "."));
  const valid = title.trim().length > 0
    && (isCustom || (Number.isFinite(targetValue) && targetValue > 0 && (type !== "win_rate" || targetValue <= 100)));

  const fail = (e: unknown) => {
    if (isPlanLimit(e)) { onClose(); openUpgrade(); return; }
    console.error(e);
    setError(t.form.error);
  };

  const save = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      if (!goal) {
        await createGoal(isCustom
          ? { title: title.trim(), type: "custom", targetValue: 1, recurrence: "none" }
          : { title: title.trim(), type, targetValue, recurrence: period === "none" ? "daily" : period });
      } else {
        // Édition : on n'envoie que ce que le tiroir affiche. Un goal ponctuel de l'Advanced
        // garde sa récurrence « none » et son échéance tant que l'utilisateur ne choisit pas de période.
        const typeChanged = type !== goal.type;
        const toCustom = isCustom && typeChanged;
        const needsPeriod = !isCustom && typeChanged && goal.recurrence === "none" && period === "none";
        await updateGoal({
          id: goal.id,
          title: title.trim(),
          type,
          targetValue: isCustom ? 1 : targetValue,
          ...(toCustom ? { recurrence: "none", deadline: null } : {}),
          ...(!isCustom && (periodTouched || needsPeriod) ? { recurrence: period === "none" ? "daily" : period } : {}),
        });
      }
      onClose();
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!goal || busy) return;
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setBusy(true);
    try { await deleteGoal({ id: goal.id }); onClose(); } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const types = (["pnl", "win_rate", "trades_count", "custom"] as GoalType[]).map(id => ({ id, label: t.goals.type[id] }));
  const periods = (["daily", "weekly", "monthly"] as Period[]).map(id => ({ id, label: t.goals.period[id] }));

  return (
    <LiteDrawer
      open={open}
      title={goal ? t.goals.edit : t.goals.add}
      onClose={onClose}
      footer={
        <>
          {goal ? (
            <Button variant="ghost" className="text-pnlNegative" onClick={remove} disabled={busy}>
              <Trash2 className="h-4 w-4 mr-1.5" />{confirmDelete ? t.form.confirmDelete : t.form.delete}
            </Button>
          ) : <span />}
          <Button onClick={save} disabled={!valid || busy}>{t.form.save}</Button>
        </>
      }
    >
      <div>
        <label className={labelCls} htmlFor="lite-goal-title">{t.form.goalTitle}</label>
        <Input id="lite-goal-title" value={title} placeholder={t.form.goalTitlePlaceholder} onChange={e => setTitle(e.target.value)} autoFocus />
      </div>
      <div>
        <div className={labelCls}>{t.form.goalType}</div>
        <Segmented value={type} options={types} onChange={setType} />
      </div>
      {isCustom ? (
        <p className="text-sm text-muted-foreground">{t.form.customHint}</p>
      ) : (
        <>
          <div>
            <label className={labelCls} htmlFor="lite-goal-target">{t.form.goalTarget}</label>
            <div className="relative">
              <Input id="lite-goal-target" className="font-mono pr-10" inputMode="decimal" value={target} onChange={e => setTarget(e.target.value)} />
              {UNIT[type] && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-mono">{UNIT[type]}</span>}
            </div>
          </div>
          <div>
            <div className={labelCls}>{t.form.goalPeriod}</div>
            <Segmented value={period} options={periods} onChange={p => { setPeriod(p); setPeriodTouched(true); }} />
          </div>
        </>
      )}
      {error && <p className="text-sm text-pnlNegative">{error}</p>}
    </LiteDrawer>
  );
}
